import React, { useEffect, useState } from "react";
import { Input, Space, Button, Spin, notification } from "antd";
import { ReactSVG } from "react-svg";
import { Link } from "react-router-dom";
import socketIOClient from "socket.io-client";
import Cookies from "js-cookie";
import { filter, forEach, sortBy } from "lodash";

import withPage from "./withPage";

import RoutePlanLeft from "../RoutePlan/RoutePlanLeft";
import OrdersTable from "../Table/Orders";
import UploadForm from "../Forms/Orders/Upload";
import StopsMapbox from "../Mapbox/StopsMapbox";

import ItemContainer from "../../modules/routePlan/containers/Item";
import { getTranslation, getStopsOrder, splitFirstOccurrence } from "../../utils/helper";
import { DATE_FORMAT_QUERY, DATE_FORMAT_FRONTEND, UPLOADED_TIME_ORDER } from "../../constants";

import "./RoutePlanDetail.scss";
import "./RoutePlanAddOrders.scss";
import { doGetLastOrder } from "../../modules/order/api";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// Extend Day.js with UTC plugin
dayjs.extend(utc);

const socketURI = process.env.REACT_APP_SOCKET_URI || "";
const socket = socketIOClient(socketURI);

const RoutePlanAddOrdersPage = ({
  history,
  isFetching,
  id,
  routePlan,
  orders,
  metadataOrders,
  query,
  doGetAllOrders,
  doUploadOrders,
  doAddOrdersRoutePlan,
  handleFailedOptimize,
  user,
  pushTo,
}) => {
  const setting = user.company;

  const currentPage = Number(query?.currentPage) || 1;
  const pagination = {
    current: currentPage,
    pageSize: 1000,
    pageSizeOptions: [1000],
    total: metadataOrders.totalItems,
    showTotal: total => `${total} ${getTranslation(setting, "Orders", "Orders")}`,
  };

  const routePlanType = routePlan?.type || "PDP";
  const deliveryDate = dayjs(routePlan?.deliveryDate).utc().format(DATE_FORMAT_FRONTEND);
  const deliveryDateQuery = dayjs(routePlan?.deliveryDate).utc().format(DATE_FORMAT_QUERY);
  const defaultParams = {
    deliveryDate: routePlan ? dayjs(routePlan.deliveryDate).utc().format(DATE_FORMAT_QUERY) : null,
    status: "DRAFT,ERROR,GEOCODING",
    routePlanType,
  };

  const routePlanOrders = routePlan?.orders || [];
  const routes = routePlan?.drivers || [];

  const [selectedOrders, setSelectedOrders] = useState(null);
  const [state, setState] = useState({});
  const [stops, setStops] = useState([]);
  const [defaultCenter, setDefaultCenter] = useState();
  const [isGetLastOrder, setIsGetLastOrder] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const canEdit = !user?.roles?.includes("user");

  useEffect(() => {
    if (orders && !selectedOrders) {
      const orders_filtered = orders.filter(o => o.status !== "ERROR" && o.status !== "GEOCODING");
      setSelectedOrders(orders_filtered);
    }
  }, [orders]);

  useEffect(() => {
    const calculateStops = () => {
      let stopsT = [];
      forEach(routePlanOrders || [], order => {
        stopsT = stopsT.concat(getStopsOrder(order));
      });
      forEach(selectedOrders || [], order => {
        stopsT = stopsT.concat(getStopsOrder(order));
      });
      return stopsT;
    };

    const newStops = calculateStops();
    if (JSON.stringify(newStops) !== JSON.stringify(stops)) {
      setStops(newStops);
    }
  }, [routePlanOrders, selectedOrders]);

  useEffect(() => {
    const fetchOrders = async () => {
      if (routePlan?.deliveryDate) {
        await doGetAllOrders({
          ...defaultParams,
          currentPage: 1,
          itemsPerPage: 1000,
          ...query,
        });
      }
    };
    fetchOrders();
  }, [routePlan?.deliveryDate]);

  useEffect(() => {
    if (query.stopId && stops.length > 0) {
      const stop = stops.find(s => s.id === query.stopId);
      if (stop) {
        onClickStop(stop);
      }
    }
  }, [query.stopId, stops]);

  useEffect(() => {
    const initializeLastOrder = async () => {
      if (routePlanOrders.length === 0 && routes.length === 0) {
        await getLastOrder();
      } else {
        setIsGetLastOrder(true);
      }
    };
    initializeLastOrder();
  }, [routePlanOrders.length, routes.length]);

  const onClosePopup = () => {
    setState({ ...state, stopClicked: { id: "closed" }, routeClicked: {} });
  };

  const getLastOrder = async () => {
    const { response: order } = await doGetLastOrder();
    if (order?.deliveryLat && order?.deliveryLng) setDefaultCenter({ lng: order.deliveryLng, lat: order.deliveryLat });
    setIsGetLastOrder(true);
  };

  const onClickStop = stop => {
    setState({ ...state, stopClicked: stop, routeClicked: {} });
  };

  const onClickRoute = route => {
    setState({ ...state, stopClicked: {}, routeClicked: route });
  };

  const handleAddOrders = async selectedOrders => {
    const items = filter(selectedOrders, o => ["ERROR", "GEOCODING"].includes(o.status));
    if (!items.length) {
      await doAddOrdersRoutePlan({
        id: id,
        orders: selectedOrders.map(s => s._id),
      });
      Cookies.remove(UPLOADED_TIME_ORDER);
      Cookies.set("PreAction", "AddOrders");
      history.push(`/route-plans/${id}/add-drivers`);
    } else {
      notification.error({
        message:
          items
            .filter((item, i) => i < 5)
            .map(item => item.orderCode)
            .join(", ") +
          ` ${items.length === 1 ? "has" : "have"} incorrect data. Please correct ${
            items.length === 1 ? "it" : "them"
          } before adding ${items.length === 1 ? "it" : "them"} to the route.`,
        placement: "bottomRight",
      });
    }
  };

  useEffect(() => {
    const handler = async data => {
      if (data.event === "orders.geocoding.completed") {
        const uploadedTime = Cookies.get(UPLOADED_TIME_ORDER);
        if (!uploadedTime) return;
        await doGetAllOrders({
          ...defaultParams,
          uploadedTime,
          currentPage: 1,
          itemsPerPage: 1000,
          ...query,
        });
      }
      if (data.event === "routePlan.optimizing.error" && data.payload.routePlanId === id) {
        console.log("routePlan.optimizing.error");
        const drivers = routePlan?.drivers?.map(r => r.driver) || [];
        handleFailedOptimize();
        let message = data.payload.message;
        if (message.includes("Visit")) {
          const split1 = splitFirstOccurrence(message, ":");
          const split2 = splitFirstOccurrence(split1[0], " ");
          if (split1.length === 2 && split2.length === 2 && split2[1].includes("_")) {
            const split3 = split2[1].split("_");
            const orderId = split3[0];
            const order = routePlanOrders.find(o => o._id == orderId);
            if (order) message = order.orderCode + ": " + split1[1];
          }
        } else if (message.includes("_pickup") || message.includes("_dropoff")) {
          const message_arr = message?.split(" ") || [];
          message = message_arr
            .map(m => {
              if (m.includes("_pickup")) {
                const order_type = m.split("_");
                const orderId = order_type[0];
                const order = routePlanOrders.find(o => o._id == orderId);
                if (order) {
                  return `Pickup ${order.orderCode}`;
                } else {
                  return m;
                }
              } else if (m.includes("_dropoff")) {
                const order_type = m.split("_");
                const orderId = order_type[0];
                const order = routePlanOrders.find(o => o._id == orderId);
                if (order) {
                  return `Delivery ${order.orderCode}`;
                } else {
                  return m;
                }
              } else {
                return m;
              }
            })
            .join(" ");
        } else {
          try {
            const split1 = splitFirstOccurrence(message, ":");
            const driver = drivers.find(d => d._id === split1[0]);
            if (driver) {
              message = driver.name + ": " + split1[1];
            } else {
              const order = routePlanOrders.find(o => o._id === split1[0]);
              if (order) message = order.orderCode + ": " + split1[1];
            }
          } catch (e) {
            console.log(e);
          }
        }
        api.error({
          message,
          placement: "bottomRight",
        });
      }
    };

    socket.connect();
    socket.on(user.company?._id, handler);
    return () => {
      socket.off(user.company?._id, handler);
      if (Cookies.get(UPLOADED_TIME_ORDER)) Cookies.remove(UPLOADED_TIME_ORDER);
    };
  }, [routePlan, id, user.company?._id]);

  const customUploadOrders = async formData => {
    const uploadedTime = Cookies.get(UPLOADED_TIME_ORDER);
    await doUploadOrders(formData);
    doGetAllOrders({
      ...defaultParams,
      uploadedTime,
      currentPage: 1,
      itemsPerPage: 1000,
      ...query,
    });
  };

  const handleBulkUpdate = selectedOrders => {
    return {
      title: `${selectedOrders.length} ${getTranslation(setting, "Route Plans", "orders selected")}`,
      actions: (
        <Space>
          <Button
            className="btn-add-to-route"
            size="large"
            key="AddToRoute"
            onClick={() => handleAddOrders(selectedOrders)}
            type="primary"
            disabled={selectedOrders.length <= 0}>
            {getTranslation(setting, "Route Plans", "Add to Route")}
            <ReactSVG className="icon" src="/icons/add-to-route.svg" />
          </Button>
        </Space>
      ),
    };
  };

  const handleOnSearch = e => {
    console(e.target.value);
  };

  const handleFilter = (params, updatePath) => {
    const uploadedTime = Cookies.get(UPLOADED_TIME_ORDER);
    if (!uploadedTime) return;
    if (updatePath) {
      pushTo({ query: params });
    }
    const currentPage = Number(params?.currentPage) || 1;
    const itemsPerPage = 1000;
    doGetAllOrders({
      ...defaultParams,
      uploadedTime,
      currentPage,
      itemsPerPage,
      ...params,
    });
  };

  const handleTableChange = params => {
    handleFilter(params, true);
  };

  const handleMenuClick = type => {
    setIsMenuOpen(false);
    if (type === "upload") {
      history.push(
        `/orders/upload?redirectUrl=/route-plans/${routePlan?._id}/add-orders&deliveryDate=${deliveryDateQuery}&routePlanType=${routePlan?.type}`,
      );
    } else if (type === "new") {
      history.push(
        `/orders/new?redirectUrl=/route-plans/${routePlan?._id}/add-orders&deliveryDate=${deliveryDateQuery}&routePlanType=${routePlan?.type}`,
      );
    }
  };

  if (isFetching || routePlan?._id !== id) {
    return <Spin style={{ paddingTop: 100, width: "100%" }} />;
  }

  return (
    <div className="Content ContentFull RoutePlanDetailPage RoutePlanAddOrdersPage">
      {contextHolder}
      <RoutePlanLeft routePlan={routePlan} />
      <div className="ContentMain">
        <div className="MainLeft">
          <div className="MainLeft__Header">
            <div className="MainLeft__Header__Title AddOrders">
              <div className="HeaderLeft">
                <Link to="/route-plans">{getTranslation(setting, "Route Plans", "Route Plans")}</Link>
                <ReactSVG className="icon" src="/icons/arrow-forward.svg" />
                <Link to={`/route-plans/${routePlan?._id}/orders`}>
                  <span>
                    {routePlan?.name} (<span className="Capitalize">{deliveryDate}</span>)
                  </span>
                </Link>
                <ReactSVG className="icon" src="/icons/arrow-forward.svg" />
                <span className="Link">{getTranslation(setting, "Route Plans", "Add Orders")}</span>
              </div>
              <div className="HeaderRight">
                <div className="custom-dropdown">
                  <Button size="large" className="NewButton" type="primary" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    {getTranslation(setting, "Orders", "New Order")}
                    <ReactSVG className="icon" src="/icons/add.svg" />
                  </Button>
                  {isMenuOpen && (
                    <div className="custom-menu">
                      <div className="custom-menu-item" onClick={() => handleMenuClick("upload")}>
                        <Button type="link">
                          {getTranslation(setting, "Orders", "Upload")}
                          <ReactSVG className="icon" src="/icons/upload.svg" />
                        </Button>
                      </div>
                      <div className="custom-menu-item" onClick={() => handleMenuClick("new")}>
                        <Button type="link">
                          {getTranslation(setting, "Orders", "Single Order")}
                          <ReactSVG className="icon" src="/icons/manual.svg" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="MainLeft__Header__Search">
              <Input.Search
                size="large"
                placeholder={getTranslation(setting, "Route Plans", "Search Anything")}
                onChange={handleOnSearch}
              />
            </div>
          </div>
          {orders?.length > 0 ? (
            <OrdersTable
              setting={setting}
              rowKey="_id"
              loading={isFetching}
              pagination={pagination}
              dataSource={sortBy(orders, [o => o.status !== "ERROR", o => o.status !== "GEOCODING"])}
              scrollHeight="calc(100vh - 610px)"
              bulkUpdate={handleBulkUpdate}
              onChange2={handleTableChange}
              query={query}
              defaultSelectedItems={orders}
              routePlanType={routePlanType}
              selectedItems={selectedOrders}
              setSelectedItems={setSelectedOrders}
              getCheckboxProps={record => ({ disabled: ["ERROR", "GEOCODING"].includes(record.status) })}
            />
          ) : (
            <UploadForm
              isFetching={isFetching}
              doUploadOrders={customUploadOrders}
              hideDocs
              query={{ routePlanType, deliveryDate }}
              routePlan={routePlan}
              history={history}
            />
          )}
        </div>
        <div className="MainRight">
          {isGetLastOrder && (
            <StopsMapbox
              routes={canEdit ? routes : []}
              stops={filter(stops, s => s.lat && s.lng)}
              orders={routePlanOrders}
              stopClicked={state.stopClicked}
              onClickStop={onClickStop}
              routeClicked={state.routeClicked}
              onClickRoute={onClickRoute}
              onClosePopup={onClosePopup}
              defaultCenter={defaultCenter}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default withPage(ItemContainer(RoutePlanAddOrdersPage));
